import { makeObservable, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import type { Aggregate } from 'edit/Aggregate';
import type { Handle } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

/**
 * One output handle, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * `label`, `condition` and `returnCodes` are plain public observables — read/write directly. `id` never changes after
 * construction (loaded from the wire, or a fresh uuid for a new handle) and is preserved through `toSave` so Flow edges keep
 * pointing at the right handle. Which of `condition` / `return_codes` is persisted follows the parent node's type, not a
 * per-handle flag — a definition's type is fixed for the whole edit.
 */
export class EditableHandle {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableHandle>;
  condition: string | null = null;
  /** Stable identity for React list keys / drag ids and the Flow graph's edge references; never changes after construction. */
  readonly id: string;
  label: string | null = null;
  returnCodes: string | null = null;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds the fields from a loaded handle; when absent, a fresh handle with a new id.
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableHandle>, wire?: Handle) {
    this._aggregate = aggregate;
    this.id = wire?.id ?? uuidv4();
    makeObservable(this, {
      condition: observable,
      label: observable,
      returnCodes: observable,
    });
    if (wire !== undefined) {
      this.label = wire.label ?? null;
      this.condition = wire.condition ?? null;
      this.returnCodes = wire.return_codes ?? null;
    }
  }

  /** Remove this handle from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape, preserving `id`. A `conditions` node persists `condition`; every other type persists
   * `return_codes`. `null` fields coerce to '' (the backend stores the value verbatim; an empty string is "not set").
   */
  toSave(): Handle {
    const isConditions = this._aggregate.parent.type === 'conditions';
    return isConditions
      ? { id: this.id, label: this.label ?? '', condition: this.condition ?? '' }
      : { id: this.id, label: this.label ?? '', return_codes: this.returnCodes ?? '' };
  }
}
